import { useAuth } from '@clerk/clerk-react'
import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { AuthNotice } from '@/components/auth/AuthNotice'
import { DropVerifying } from '@/components/drop/DropVerifying'
import { Button } from '@/components/ui/Button'
import { CountryField } from '@/components/ui/CountryField'
import { FIELD, FieldSelect, LABEL } from '@/components/ui/formFields'
import { GlassCard } from '@/components/ui/GlassCard'
import { useMe } from '@/context/MeProvider'
import { CATEGORIES, CATEGORY_OTHER } from '@/data/categories'
import { useAuthRedirect } from '@/hooks/useAuthRedirect'
import { ApiError } from '@/lib/api/client'
import { createListing } from '@/lib/api/listings'
import { cn } from '@/lib/cn'
import {
  clearDropDraft,
  loadDropDraft,
  saveDropDraft,
  type DropDraft,
} from '@/lib/dropDraft'
import { toDatetimeLocal, formatWaitUntil } from '@/lib/time'
import type { ListingType } from '@/lib/types/listing'

/** Keep the verifying scene on screen even if the check is instant. */
const MIN_VERIFY_MS = 2000

/** Matches api `listing_posts_per_window`. */
const POSTS_PER_WINDOW = 1
/** Matches api `listing_max_expiry_days`. */
const MAX_EXPIRY_DAYS = 30
const MS_PER_DAY = 24 * 60 * 60 * 1000

function defaultExpiry(): string {
  return toDatetimeLocal(new Date(Date.now() + 7 * MS_PER_DAY))
}

function emptyDraft(): DropDraft {
  return {
    type: 'referral',
    destinationUrl: '',
    brand: '',
    category: '',
    categoryOther: '',
    country: '',
    benefitClaim: '',
    expiresAt: defaultExpiry(),
  }
}

/** A restored draft's expiry may now be in the past — reset it if so. */
function freshenExpiry(draft: DropDraft): DropDraft {
  const parsed = new Date(draft.expiresAt).getTime()
  if (Number.isNaN(parsed) || parsed <= Date.now()) {
    return { ...draft, expiresAt: defaultExpiry() }
  }
  return draft
}

function blankToUndefined(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

function postAgainCopy(nextPostAt: string | null | undefined, now = Date.now()): string {
  if (!nextPostAt) {
    return 'You can post again 24 hours after your last one.'
  }
  const wait = formatWaitUntil(nextPostAt, now)
  if (!wait || wait === 'now') {
    return 'You can post again now.'
  }
  return `You can post again ${wait}.`
}

function postError(
  err: unknown,
  type: ListingType,
  nextPostAt?: string | null,
): string {
  if (!(err instanceof ApiError)) {
    return "Couldn't post this link."
  }
  if (err.status === 401) return 'Sign in to post a link.'
  if (err.status === 403 && err.detail === 'Email not verified') {
    return 'Verify your email to post a link.'
  }
  if (err.status === 403 && err.detail === 'Account is not active') {
    return 'Your account is suspended.'
  }
  if (err.status === 409) {
    return type === 'referral'
      ? 'This referral link is already on dizcant.'
      : "You've already posted this discount link."
  }
  if (err.status === 429) {
    return `That's your post for now. ${postAgainCopy(nextPostAt)}`
  }
  return err.detail
}

export function DropForm({
  onVerifyingChange,
}: {
  onVerifyingChange?: (verifying: boolean) => void
}) {
  const formId = useId()
  const navigate = useNavigate()
  const { isLoaded, isSignedIn, userId, getToken } = useAuth()
  const { goToSignIn, goToSignUp } = useAuthRedirect()
  const { me, loading: meLoading, error: meError, refresh, canMutate } = useMe()

  const [draft, setDraft] = useState<DropDraft>(emptyDraft)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [restoredNotice, setRestoredNotice] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const restored = useRef(false)
  const edited = useRef(false)

  function setField<K extends keyof DropDraft>(key: K, value: DropDraft[K]) {
    edited.current = true
    setDraft((current) => ({ ...current, [key]: value }))
  }

  // Restore a persisted draft, once. A guest draft comes back immediately; a
  // signed-in draft waits for `isLoaded` so we can match the owner. Skip if the
  // user already started typing, so the notice isn't shown spuriously.
  useEffect(() => {
    if (restored.current || edited.current) {
      return
    }
    const saved = loadDropDraft(userId, { authSettled: isLoaded })
    if (saved) {
      restored.current = true
      edited.current = true
      setDraft(freshenExpiry(saved))
      setRestoredNotice(true)
    } else if (isLoaded) {
      restored.current = true
    }
  }, [isLoaded, userId])

  // Persist edits. Not gated on `isLoaded` — a guest's draft (ownerKey "guest")
  // must survive even if Clerk is slow or unreachable. `edited` skips the
  // initial default state so we never clobber a stored draft before restore.
  useEffect(() => {
    if (!edited.current) {
      return
    }
    saveDropDraft(draft, userId)
  }, [draft, userId])

  function startOver() {
    clearDropDraft()
    setDraft(emptyDraft())
    setRestoredNotice(false)
    setError(null)
  }

  const minExpiry = toDatetimeLocal(new Date(Date.now() + 60_000))
  const maxExpiry = toDatetimeLocal(
    new Date(Date.now() + MAX_EXPIRY_DAYS * MS_PER_DAY),
  )
  const nextPostAt = me?.nextPostAt ?? null
  const waitMs = nextPostAt ? Date.parse(nextPostAt) - now : 0
  const uncapped = me?.role === 'admin'
  const capReached =
    me !== null &&
    !uncapped &&
    me.postsUsed >= POSTS_PER_WINDOW &&
    waitMs > 0
  const blocked =
    capReached || (isSignedIn && !canMutate) || submitting || meLoading

  useEffect(() => {
    if (!capReached) {
      return
    }
    const id = window.setInterval(() => {
      setNow(Date.now())
    }, 30_000)
    return () => {
      window.clearInterval(id)
    }
  }, [capReached])

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)

    if (!isSignedIn) {
      goToSignIn()
      return
    }
    if (!canMutate || capReached) {
      return
    }

    const url = draft.destinationUrl.trim()
    if (!url.startsWith('https://')) {
      setError('Link must start with https://')
      return
    }

    const expiry = new Date(draft.expiresAt)
    const now = Date.now()
    if (Number.isNaN(expiry.getTime()) || expiry.getTime() <= now) {
      setError('Expiry must be in the future')
      return
    }
    if (expiry.getTime() > now + MAX_EXPIRY_DAYS * MS_PER_DAY) {
      setError(`Expiry must be within ${MAX_EXPIRY_DAYS} days`)
      return
    }

    const categoryValue =
      draft.category === CATEGORY_OTHER
        ? blankToUndefined(draft.categoryOther)
        : blankToUndefined(draft.category)
    if (draft.category === CATEGORY_OTHER && !categoryValue) {
      setError('Enter a category')
      return
    }

    setSubmitting(true)
    onVerifyingChange?.(true)
    const started = Date.now()
    try {
      const listing = await createListing(
        {
          type: draft.type,
          destinationUrl: url,
          brand: draft.brand.trim(),
          category: categoryValue ?? null,
          country: blankToUndefined(draft.country) ?? null,
          benefitClaim: blankToUndefined(draft.benefitClaim) ?? null,
          expiresAt: expiry.toISOString(),
        },
        getToken,
      )
      const wait = MIN_VERIFY_MS - (Date.now() - started)
      if (wait > 0) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, wait)
        })
      }
      clearDropDraft()
      await refresh()
      if (listing.status === 'live') {
        navigate('/discover')
        return
      }
      navigate('/stash', { state: { checkHeld: true } })
    } catch (err) {
      onVerifyingChange?.(false)
      if (err instanceof ApiError && err.status === 401) {
        goToSignIn()
        return
      }
      setError(postError(err, draft.type, nextPostAt))
      setSubmitting(false)
    }
  }

  if (submitting) {
    return (
      <GlassCard>
        <DropVerifying />
      </GlassCard>
    )
  }

  return (
    <GlassCard className="p-6 md:p-8">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="space-y-6"
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className={LABEL}>Posts used</p>
          <p className="font-body text-body-sm text-fg">
            {isSignedIn
              ? meLoading || me === null
                ? 'Loading…'
                : uncapped
                  ? 'Unlimited'
                  : `${me.postsUsed} / ${POSTS_PER_WINDOW}`
              : `Sign in to post (${POSTS_PER_WINDOW} per 24 hours)`}
          </p>
        </div>
        {meError && (
          <p className="font-body text-label-sm text-error" role="alert">
            {meError}
          </p>
        )}
        {capReached && (
          <div
            role="status"
            className="border-primary-container/30 bg-primary-container/15 rounded-lg border px-4 py-3"
          >
            <p className="font-body text-body-md text-fg font-semibold">
              That&apos;s your post for now.
            </p>
            <p className="font-body text-body-sm text-fg mt-1">
              {postAgainCopy(nextPostAt, now)}
            </p>
          </div>
        )}
        {restoredNotice && (
          <p
            className="font-body text-body-sm text-fg-muted"
            aria-live="polite"
          >
            We kept your draft.{' '}
            <button
              type="button"
              onClick={startOver}
              className="text-primary hover:text-primary-fixed-dim underline underline-offset-2"
            >
              Start over
            </button>
          </p>
        )}
        <AuthNotice action="post" />

        <fieldset className="space-y-3" disabled={submitting}>
          <legend className={LABEL}>Link type</legend>
          <div
            role="group"
            aria-label="Link type"
            className="flex flex-wrap gap-2"
          >
            {(
              [
                { label: 'Referral', value: 'referral' },
                { label: 'Discount', value: 'discount' },
              ] as const
            ).map((option) => {
              const selected = draft.type === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    setField('type', option.value)
                  }}
                  className={cn(
                    'font-body text-label-caps min-h-11 rounded-full border px-4 uppercase transition-colors',
                    selected
                      ? 'border-primary-container/30 bg-primary-container/15 text-primary'
                      : 'border-glass-border text-fg-muted hover:text-fg bg-white/5',
                  )}
                >
                  {option.label}
                </button>
              )
            })}
          </div>
          <p className="font-body text-body-sm text-fg-muted">
            {draft.type === 'referral'
              ? 'The brand pays you for the referral, so the claimer gets the point.'
              : 'The claimer saves money, so you get the point.'}
          </p>
        </fieldset>

        <div className="space-y-2">
          <label htmlFor={`${formId}-url`} className={LABEL}>
            Destination URL
          </label>
          <input
            id={`${formId}-url`}
            type="url"
            required
            maxLength={2048}
            value={draft.destinationUrl}
            onChange={(event) => {
              setField('destinationUrl', event.target.value)
            }}
            placeholder="https://"
            autoComplete="url"
            disabled={blocked}
            className={FIELD}
          />
        </div>

        <div className="space-y-2">
          <label htmlFor={`${formId}-brand`} className={LABEL}>
            Brand
          </label>
          <input
            id={`${formId}-brand`}
            type="text"
            required
            value={draft.brand}
            onChange={(event) => {
              setField('brand', event.target.value)
            }}
            disabled={blocked}
            className={FIELD}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <label htmlFor={`${formId}-category`} className={LABEL}>
              Category
            </label>
            <FieldSelect
              id={`${formId}-category`}
              value={draft.category}
              onChange={(event) => {
                setField('category', event.target.value)
              }}
              disabled={blocked}
            >
              <option value="">None</option>
              {CATEGORIES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
              <option value={CATEGORY_OTHER}>Other</option>
            </FieldSelect>
            {draft.category === CATEGORY_OTHER && (
              <input
                id={`${formId}-category-other`}
                type="text"
                required
                value={draft.categoryOther}
                onChange={(event) => {
                  setField('categoryOther', event.target.value)
                }}
                placeholder="Your category"
                disabled={blocked}
                className={FIELD}
              />
            )}
          </div>
          <CountryField
            id={`${formId}-country`}
            value={draft.country}
            onChange={(next) => {
              setField('country', next)
            }}
            disabled={blocked}
            hint="Where the offer works. Leave it blank if it works anywhere."
          />
        </div>

        <div className="space-y-2">
          <label htmlFor={`${formId}-benefit`} className={LABEL}>
            The offer
          </label>
          <textarea
            id={`${formId}-benefit`}
            value={draft.benefitClaim}
            onChange={(event) => {
              setField('benefitClaim', event.target.value)
            }}
            rows={3}
            placeholder={
              draft.type === 'referral'
                ? 'e.g. $20 off their first order (optional)'
                : 'e.g. 15% off sitewide (optional)'
            }
            disabled={blocked}
            className={cn(FIELD, 'min-h-24 resize-y')}
          />
        </div>

        <div className="space-y-2">
          <label htmlFor={`${formId}-expires`} className={LABEL}>
            Expires
          </label>
          <input
            id={`${formId}-expires`}
            type="datetime-local"
            required
            min={minExpiry}
            max={maxExpiry}
            value={draft.expiresAt}
            onChange={(event) => {
              setField('expiresAt', event.target.value)
            }}
            disabled={blocked}
            className={FIELD}
          />
          <p className="font-body text-label-sm text-fg-muted">
            Use the offer&apos;s real end date — up to {MAX_EXPIRY_DAYS} days
            out.
          </p>
        </div>

        {error && (
          <p className="font-body text-body-sm text-error" role="alert">
            {error}
          </p>
        )}

        {isSignedIn ? (
          <Button
            type="submit"
            variant="gradient"
            size="lg"
            disabled={blocked}
            className="w-full sm:w-auto"
          >
            {submitting ? 'Posting…' : 'Post link'}
          </Button>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              type="button"
              variant="gradient"
              size="lg"
              className="w-full sm:w-auto"
              onClick={goToSignUp}
            >
              Create an account to post
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="lg"
              className="w-full sm:w-auto"
              onClick={goToSignIn}
            >
              I already have one
            </Button>
          </div>
        )}
      </form>
    </GlassCard>
  )
}
